'use strict';

// The organisation's week start (W1, Cave Inn brief, 2026-11-05):
// `organisation.weekStartsOn`, an integer 0–6 where 0 is Sunday and 6 is
// Saturday, chosen in Settings → Global settings by a Manager or Systems
// Administrator. Absent means Sunday, which is what every week view did
// before the setting existed.
//
// ⚠ WHAT IT DOES NOT MEAN.
// - It is NOT a pay fact. Weekday rules in awards, wage presets, opening
//   hours and weekly availability are keyed by weekday NAME and do not move
//   when the week start moves.
// - These helpers do NOT convert time zones. They work on the dayjs objects
//   they are given, in whatever zone those objects are already in. A caller
//   that needs the organisation's zone converts before calling.
// - A stored value that is not an integer 0–6 does NOT mean "no week". It
//   means Sunday, the same as absent ('' , null, '1', 1.5 and true are all
//   Sunday; CLAUDE.md, "blank is not zero").
//
// firestore.rules repeats the 0–6 bound (rules cannot import); the rules test
// src/tests/rules/orgWeekStartsOnGate.rules.test.js pins it.

const DEFAULT_WEEK_STARTS_ON = 0;

const WEEKDAY_NAMES = Object.freeze([
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
]);

const isWeekStartsOn = (v) => Number.isInteger(v) && v >= 0 && v <= 6;

/** The organisation's week start: the stored value when it is an integer 0–6, else Sunday (0). */
function weekStartsOn(org) {
  const v = org && typeof org === 'object' ? org.weekStartsOn : undefined;
  return isWeekStartsOn(v) ? v : DEFAULT_WEEK_STARTS_ON;
}

/** The seven weekday indexes in the organisation's order, its first day first. */
function weekdayOrder(org) {
  const s = weekStartsOn(org);
  return [0, 1, 2, 3, 4, 5, 6].map((i) => (s + i) % 7);
}

/**
 * The week containing `date`: `start` is the start of the organisation's first
 * weekday on or before `date`, `end` is the end of the sixth day after it.
 * `date` is a dayjs object; both results are dayjs objects in its zone.
 */
function weekRangeContaining(date, org) {
  const s = weekStartsOn(org);
  const day = date.startOf('day');
  const start = day.subtract((day.day() - s + 7) % 7, 'day');
  return { start, end: start.add(6, 'day').endOf('day') };
}

/**
 * Where a saved schedule template's shift lands when loaded (ruled
 * 2026-11-04: by weekday, never by date offset). A template stores real dates;
 * the shift's weekday is read from its own date. The template is cut into
 * seven-day blocks from its saved start, and a shift in block k lands on the
 * same weekday inside the k-th seven days from `targetStart`. So a Sunday shift
 * from a Sunday-start template lands on Sunday whether the target week starts
 * on Sunday or on Monday. All three arguments are dayjs objects.
 */
function templateDateByWeekday(shiftDate, templateStart, targetStart) {
  const target = targetStart.startOf('day');
  const block = Math.floor(shiftDate.startOf('day').diff(templateStart.startOf('day'), 'day') / 7);
  return target.add(7 * block + ((shiftDate.day() - target.day() + 7) % 7), 'day');
}

module.exports = {
  DEFAULT_WEEK_STARTS_ON,
  WEEKDAY_NAMES,
  isWeekStartsOn,
  weekStartsOn,
  weekdayOrder,
  weekRangeContaining,
  templateDateByWeekday,
};
